import React from 'react';
import { useAuth } from '../context/AuthContext';
import { PublicLayout } from '../components/landing/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { Scale } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-cinzel font-bold text-lg text-foreground mb-3 border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">
      {children}
    </div>
  </div>
);

export default function MentionsLegalesPage() {
  const { language } = useAuth();
  const title = language === 'fr' ? 'Mentions Légales' : 'Legal Notice';

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="flex justify-center mb-4">
            <Scale className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {title}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            {language === 'fr'
              ? 'Conformément à la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l\'économie numérique (LCEN)'
              : 'In accordance with French law n° 2004-575 of June 21, 2004 on confidence in the digital economy'}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-border/50">
            <CardContent className="p-8">

              <Section title="1. Éditeur du site">
                <p>Le site internet <strong>WyrdMaker</strong> (ci-après « le Site ») est édité par :</p>
                <div className="mt-3 p-4 bg-muted/30 rounded-lg space-y-1">
                  <p><strong>Raison sociale :</strong> [À COMPLÉTER — Nom de la société ou nom du particulier]</p>
                  <p><strong>Forme juridique :</strong> [À COMPLÉTER — Ex : SAS, SARL, auto-entrepreneur]</p>
                  <p><strong>Capital social :</strong> [À COMPLÉTER] €</p>
                  <p><strong>Numéro SIRET :</strong> [À COMPLÉTER]</p>
                  <p><strong>Numéro RCS :</strong> [À COMPLÉTER] — Ville d'immatriculation</p>
                  <p><strong>Numéro de TVA intracommunautaire :</strong> [À COMPLÉTER]</p>
                  <p><strong>Siège social :</strong> [À COMPLÉTER — Adresse complète]</p>
                  <p><strong>Adresse e-mail :</strong> [À COMPLÉTER — contact@wyrdmaker.com]</p>
                  <p><strong>Téléphone :</strong> [À COMPLÉTER]</p>
                </div>
              </Section>

              <Section title="2. Directeur de la publication">
                <p>Le directeur de la publication est <strong>[À COMPLÉTER — Nom Prénom]</strong>, en qualité de [À COMPLÉTER — gérant / président].</p>
                <p>Adresse e-mail de contact : <strong>[À COMPLÉTER]</strong></p>
              </Section>

              <Section title="3. Hébergement">
                <p>Le Site est hébergé par :</p>
                <div className="mt-3 p-4 bg-muted/30 rounded-lg space-y-1">
                  <p><strong>Hébergeur :</strong> [À COMPLÉTER — Ex : OVH SAS, Scaleway, Hetzner]</p>
                  <p><strong>Adresse :</strong> [À COMPLÉTER]</p>
                  <p><strong>Téléphone :</strong> [À COMPLÉTER]</p>
                  <p><strong>Site web :</strong> [À COMPLÉTER]</p>
                </div>
              </Section>

              <Section title="4. Propriété intellectuelle">
                <p>
                  L'ensemble des contenus présents sur le Site (textes, graphismes, logos, icônes, images, clips audio, téléchargements numériques, compilations de données, logiciels)
                  est la propriété exclusive de l'éditeur ou de ses partenaires et est protégé par les lois françaises et internationales relatives à la propriété intellectuelle.
                </p>
                <p>
                  Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du Site, quel que soit le moyen ou le procédé utilisé,
                  est interdite, sauf autorisation écrite préalable de l'éditeur.
                </p>
                <p>
                  Les marques, logos et signes distinctifs reproduits sur le Site sont protégés au titre de la propriété intellectuelle. Toute reproduction sans autorisation est interdite et constitue une contrefaçon.
                </p>
              </Section>

              <Section title="5. Protection des données personnelles (RGPD)">
                <p>
                  Conformément au Règlement Général sur la Protection des Données (RGPD — Règlement UE 2016/679) et à la loi n° 78-17 du 6 janvier 1978 modifiée relative
                  à l'informatique, aux fichiers et aux libertés, l'éditeur s'engage à protéger les données personnelles des utilisateurs.
                </p>
                <p>
                  Les données personnelles collectées sur le Site font l'objet d'un traitement informatique destiné à la gestion des comptes utilisateurs,
                  à la fourniture des services et à la facturation des abonnements.
                </p>
                <p>
                  Conformément à la réglementation, vous disposez des droits suivants :
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                  <li>Droit d'accès à vos données (art. 15 RGPD)</li>
                  <li>Droit de rectification (art. 16 RGPD)</li>
                  <li>Droit à l'effacement (art. 17 RGPD)</li>
                  <li>Droit à la limitation du traitement (art. 18 RGPD)</li>
                  <li>Droit à la portabilité (art. 20 RGPD)</li>
                  <li>Droit d'opposition (art. 21 RGPD)</li>
                </ul>
                <p className="mt-2">
                  Pour exercer ces droits, vous pouvez contacter le responsable de traitement à l'adresse : <strong>[À COMPLÉTER — email DPO ou contact]</strong>.
                </p>
                <p>
                  En cas de réclamation, vous avez le droit d'introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) :
                  3 Place de Fontenoy, TSA 80715 — 75334 Paris Cedex 07 — www.cnil.fr.
                </p>
              </Section>

              <Section title="6. Cookies">
                <p>
                  Le Site utilise des cookies techniques nécessaires au bon fonctionnement du service (authentification, préférences utilisateur).
                  Il peut également utiliser des cookies analytiques (mesure d'audience) et des cookies liés aux services de paiement tiers (Stripe, PayPal).
                </p>
                <p>
                  Conformément à la directive ePrivacy et aux recommandations de la CNIL, les cookies non essentiels ne sont déposés qu'après recueil de votre consentement.
                  Vous pouvez paramétrer vos préférences à tout moment via les réglages de votre navigateur ou notre gestionnaire de cookies.
                </p>
              </Section>

              <Section title="7. Liens hypertextes">
                <p>
                  Le Site peut contenir des liens vers des sites tiers. L'éditeur n'exerce aucun contrôle sur le contenu de ces sites et décline toute responsabilité quant aux
                  informations qui y sont publiées ou à l'utilisation qui pourrait en être faite.
                </p>
              </Section>

              <Section title="8. Limitation de responsabilité">
                <p>
                  L'éditeur s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur le Site. Il se réserve le droit de corriger, à tout moment
                  et sans préavis, le contenu de ses pages.
                </p>
                <p>
                  L'éditeur décline toute responsabilité pour tout dommage résultant de l'utilisation du Site, d'une interruption du service, d'un virus informatique ou de
                  toute autre cause indépendante de sa volonté.
                </p>
              </Section>

              <Section title="9. Droit applicable et juridiction compétente">
                <p>
                  Les présentes mentions légales sont régies par le droit français. En cas de litige, les tribunaux français seront seuls compétents.
                </p>
              </Section>

              <p className="text-xs text-muted-foreground/60 mt-8 pt-4 border-t border-border">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

            </CardContent>
          </Card>
        </div>
      </section>
    </PublicLayout>
  );
}
